import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { loginInputSchema } from '@orbit-sync/shared-types';
import { useAuthStore } from '../auth/authStore.js';
import { AuthLayout } from '../components/AuthLayout.js';
import { Field } from '../components/Field.js';
import { FormBanner } from '../components/FormBanner.js';
import {
  api,
  errorMessage,
  fieldErrorsFrom,
  isApiRequestError,
  type FieldErrors,
} from '../lib/api/index.js';

interface FromState {
  from?: { pathname?: string };
}

export function LoginPage() {
  const login = useAuthStore((state) => state.login);
  const pending = useAuthStore((state) => state.pending);
  // Set when the session ended without the user asking - an administrator
  // deleting the account, say - so they are told why they are here.
  const notice = useAuthStore((state) => state.notice);
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [directory, setDirectory] = useState<string | null>(null);

  // Whether a directory user name works here too - it changes the field's label.
  useEffect(() => {
    const controller = new AbortController();

    api.auth
      .options(controller.signal)
      .then((options) => setDirectory(options.directoryEnabled ? options.directoryName : null))
      .catch(() => undefined);

    return () => controller.abort();
  }, []);
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);

    // Validated with the same schema the server uses, so the obvious mistakes are
    // caught without a round-trip and the rules cannot drift apart.
    const parsed = loginInputSchema.safeParse({ email, password });

    if (!parsed.success) {
      const errors: FieldErrors = {};

      for (const issue of parsed.error.issues) {
        const path = issue.path.join('.');
        errors[path] ??= issue.message;
      }

      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});

    try {
      await login(parsed.data);
      await offerToSaveCredentials(parsed.data.email, parsed.data.password);

      const state = location.state as FromState | null;
      await navigate(state?.from?.pathname ?? '/', { replace: true });
    } catch (error) {
      // The server answers an unknown email and a wrong password identically, so
      // this message is deliberately vague - narrowing it would let the form be
      // used to discover which addresses have accounts.
      if (isApiRequestError(error) && error.status === 401) {
        setFormError(
          directory ? 'Sign-in name or password is incorrect.' : 'Email or password is incorrect.',
        );
        return;
      }

      setFieldErrors(fieldErrorsFrom(error));
      setFormError(errorMessage(error));
    }
  }

  return (
    <AuthLayout>
      {/* `method="post"` plus the autocomplete tokens below are what browsers'
          password managers key on to offer "Save password?" after sign-in. */}
      <form
        id="login-form"
        method="post"
        action="/login"
        onSubmit={(event) => void handleSubmit(event)}
        noValidate
      >
        <h1>Welcome back</h1>
        <p className="subtitle">Sign in to your Orbit Sync workspace.</p>

        <FormBanner message={formError ?? notice} />

        <Field
          label={directory ? 'Email or user name' : 'Email'}
          name="email"
          type={directory ? 'text' : 'email'}
          autoComplete="username"
          hint={directory ? `Your ${directory} user name works here too.` : undefined}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={fieldErrors.email}
          required
        />

        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={fieldErrors.password}
          required
        />

        <button type="submit" className="btn btn--primary btn--lg btn--block" disabled={pending}>
          {pending ? <span className="btn-spinner" /> : null}
          {pending ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="form-footer">
          No account? <Link to="/register">Create one</Link>
        </p>
      </form>
    </AuthLayout>
  );
}

/**
 * Asks the browser to remember these credentials, where it lets a page ask.
 *
 * The Credential Management API exists only in secure contexts (HTTPS or
 * localhost). Over plain HTTP it is absent, and the browser's own heuristics -
 * a real form, `autocomplete="username"` / `current-password`, the form going
 * away after a successful sign-in - are what trigger its save prompt instead.
 */
async function offerToSaveCredentials(email: string, password: string): Promise<void> {
  const Credential = (
    window as unknown as {
      PasswordCredential?: new (data: { id: string; password: string }) => Credential;
    }
  ).PasswordCredential;

  if (!Credential || !window.isSecureContext) {
    return;
  }

  try {
    await navigator.credentials.store(new Credential({ id: email, password }));
  } catch {
    // Declined or unsupported: nothing to do.
  }
}
